import type { PetDef } from '@vm/shared';
import { PET_BY_ID, PET_BY_NUM, petColors } from '../pets';
import {
  ACC,
  ACC_D,
  ACC_L,
  DARK,
  DARK_L,
  GLOW,
  GLOW_HI,
  LIGHT,
  METAL,
  METAL_D,
  METAL_L,
  ModelBuilder,
  PRI,
  PRI_D,
  RUBBER,
  SEC,
  SEC_D,
  SEC_L,
  WHITE,
} from './builder';
import { mix, tone } from './camo';
import type { V3 } from './designs';

/**
 * Procedural voxel models for companion pets (content/pets.ts). Pets use the weapon model
 * builder and palette slots at 3 cm voxels, so they mesh and shade like guns and marines.
 * Model space: x across, y up, z = 0 at the front: pets face -Z at yaw 0 like characters.
 * Animated pieces (wings, leaves, rotors, drill, jaw, tail...) are separate parts with a pivot and
 * an animation hint; the client skins them to bones (one draw call per material).
 */

/** Pet model voxel edge (meters). */
export const PET_VOXEL = 0.03;

// Extra palette slots (after the weapon slots, see builder.ts) -----------------------------
const EYE_W = 23;
const PUPIL = 24;
/** Eye glints: always emissive. */
const GLINT = 25;
/** Team badge: emissive team color in team modes, a light accent otherwise. */
const TEAM = 26;
const MOUTH = 27;
const TOOTH = 28;
/** Always-glowing cores (the guard's heart). */
const CORE = 29;
const CORE_HI = 30;
const PET_SLOT_COUNT = 31;

/** Team badge colors (same as marine cores). */
const TEAM_COLORS = [0xff5a4a, 0x4a9aff];

export type PetPartName = 'wingL' | 'wingR' | 'leafL' | 'leafR' | 'flower' | 'rotorL' | 'rotorR' | 'drill' | 'finL' | 'finR' | 'head' | 'jaw' | 'tail';

export interface PetPartAnim {
  /**
   * flap: swings around `offset` by ±amp; sway: slow swing by ±amp; spin: turns continuously
   * (`rate` turns per second); jaw: opens by `amp` radians when biting/chomping.
   */
  kind: 'flap' | 'sway' | 'spin' | 'jaw';
  axis: 'x' | 'y' | 'z';
  amp: number;
  /** Cycles (spin: turns) per second while following. */
  rate: number;
  /** flap: resting angle the swing is centered on (radians). */
  offset?: number;
  /** Mirror sign (left/right pairs) or opening direction (jaw). */
  sign?: number;
  /** Phase offset (cycles). */
  phase?: number;
  /** Rate multiplier while working (attacking/digging); default 2. */
  work?: number;
  /** Rate multiplier while active (healing/pinging/guarding); default 1.4. */
  active?: number;
}

export interface PetPartModel extends PetPartAnim {
  name: PetPartName;
  /** The part's voxels (same layout as the full grid). */
  grid: Uint8Array;
  /** Rotation pivot in voxel coordinates. */
  pivot: V3;
}

export interface PetModel {
  id: string;
  num: number;
  sx: number;
  sy: number;
  sz: number;
  /** The complete model (body plus every part at rest). Treat as read-only. */
  grid: Uint8Array;
  /** Colors indexed by grid value. */
  palette: number[];
  /** Grid values that glow. */
  emissive: number[];
  /** Body center in voxel coordinates: the point network positions refer to. */
  center: V3;
  /** Work point (beak, mouth, drill tip, flower, core) in voxel coordinates. */
  mouth: V3;
  parts: PetPartModel[];
  /** Largest distance of a voxel corner from the center (meters). */
  radius: number;
}

type PB = ModelBuilder<PetPartName>;

interface PetDesignMeta {
  center: V3;
  mouth: V3;
  parts: Partial<Record<PetPartName, PetPartAnim & { pivot: V3 }>>;
}

interface PetDesign {
  size: V3;
  build(b: PB): PetDesignMeta;
}

// Shape helpers --------------------------------------------------------------------------------

type Cell = (x: number, y: number, z: number) => boolean;

/** Ellipsoid of voxels; `onlyEmpty` keeps existing voxels (parts hugging the body leave it whole). */
function blob(b: PB, cx: number, cy: number, cz: number, rx: number, ry: number, rz: number, v: number, onlyEmpty = false, clip?: Cell): void {
  for (let y = Math.floor(cy - ry); y <= cy + ry; y++)
    for (let z = Math.floor(cz - rz); z <= cz + rz; z++)
      for (let x = Math.floor(cx - rx); x <= cx + rx; x++) {
        const dx = (x + 0.5 - cx) / rx;
        const dy = (y + 0.5 - cy) / ry;
        const dz = (z + 0.5 - cz) / rz;
        if (dx * dx + dy * dy + dz * dz > 1) continue;
        if (clip && !clip(x, y, z)) continue;
        if (onlyEmpty && b.get(x, y, z)) continue;
        b.set(x, y, z, v);
      }
}

/** Sets a voxel only when the cell is empty. */
function put(b: PB, x: number, y: number, z: number, v: number): void {
  if (!b.get(x, y, z)) b.set(x, y, z, v);
}

/** Recolors every voxel that passes a test (part membership is kept). */
function tint(b: PB, test: (x: number, y: number, z: number, v: number) => boolean, v: number): void {
  for (let y = 0; y < b.sy; y++)
    for (let z = 0; z < b.sz; z++)
      for (let x = 0; x < b.sx; x++) {
        const cur = b.grid[b.index(x, y, z)];
        if (cur && test(x, y, z, cur)) b.recolor(x, y, z, v);
      }
}

/** First solid voxel from the front (z = 0) in a column, or -1. */
function frontZ(b: PB, x: number, y: number): number {
  for (let z = 0; z < b.sz; z++) if (b.get(x, y, z)) return z;
  return -1;
}

/**
 * Paints a disc decal onto the front surface: columns within `r` of (cx, cy) recolor their first
 * `depth` voxels (so curved surfaces keep the color on their side faces too).
 */
function faceDisc(b: PB, cx: number, cy: number, r: number, v: number, depth = 2, minR = -1): void {
  for (let y = Math.floor(cy - r); y <= cy + r; y++)
    for (let x = Math.floor(cx - r); x <= cx + r; x++) {
      const d2 = (x + 0.5 - cx) ** 2 + (y + 0.5 - cy) ** 2;
      if (d2 > r * r || (minR > 0 && d2 <= minR * minR)) continue;
      const z0 = frontZ(b, x, y);
      if (z0 < 0) continue;
      for (let z = z0; z < z0 + depth && z < b.sz; z++) if (b.get(x, y, z)) b.recolor(x, y, z, v);
    }
}

/** Front decal on single cells. */
function faceCells(b: PB, cells: [number, number][], v: number, depth = 2): void {
  for (const [x, y] of cells) {
    const z0 = frontZ(b, x, y);
    if (z0 < 0) continue;
    for (let z = z0; z < z0 + depth && z < b.sz; z++) if (b.get(x, y, z)) b.recolor(x, y, z, v);
  }
}

/** Pushes the front surface one voxel forward over a disc (protruding eyes, cores, brows). */
function bulge(b: PB, cx: number, cy: number, r: number): void {
  for (let y = Math.floor(cy - r); y <= cy + r; y++)
    for (let x = Math.floor(cx - r); x <= cx + r; x++) {
      if ((x + 0.5 - cx) ** 2 + (y + 0.5 - cy) ** 2 > r * r) continue;
      const z0 = frontZ(b, x, y);
      if (z0 > 0) b.set(x, y, z0 - 1, b.get(x, y, z0));
    }
}

/** Copies a part to its mirror image across the model's x axis (left -> right). */
function mirrorPart(b: PB, from: PetPartName, to: PetPartName): void {
  const src = b.parts.get(from);
  if (!src) return;
  const cells: [number, number, number, number][] = [];
  for (let y = 0; y < b.sy; y++)
    for (let z = 0; z < b.sz; z++)
      for (let x = 0; x < b.sx; x++) {
        const v = src[b.index(x, y, z)];
        if (v) cells.push([b.sx - 1 - x, y, z, v]);
      }
  b.part(to);
  for (const [x, y, z, v] of cells) b.set(x, y, z, v);
  b.part(null);
}

/** Big cartoon eye on the front surface: iris ring (glows with glowing styles), pupil, glint. */
function eye(b: PB, ex: number, ey: number, iris: number, rIris: number, rPupil: number, glintDx: number, glintDy: number): void {
  faceDisc(b, ex, ey, rIris, iris);
  faceDisc(b, ex, ey, rPupil, PUPIL);
  bulge(b, ex, ey, rIris - 0.3);
  faceCells(b, [[Math.floor(ex + glintDx), Math.floor(ey + glintDy)]], GLINT, 1);
}

/** Distance from (px, py) to the nearest and second-nearest point of a hex lattice (spacing s). */
function hexEdge(px: number, py: number, s: number): number {
  let d1 = Infinity;
  let d2 = Infinity;
  for (let j = -5; j <= 5; j++)
    for (let i = -5; i <= 5; i++) {
      const hx = s * Math.sqrt(3) * (i + j / 2);
      const hy = s * 1.5 * j;
      const d = Math.hypot(px - hx, py - hy);
      if (d < d1) {
        d2 = d1;
        d1 = d;
      } else if (d < d2) d2 = d;
    }
  return d2 - d1;
}

// Designs (one per pet id) ----------------------------------------------------------------------

const DESIGNS: Record<string, PetDesign> = {
  /** Scout: a round owl with big eyes, ear tufts and little wings. */
  owlet: {
    size: [21, 19, 17],
    build(b) {
      const cx = 10.5;
      blob(b, cx, 8.5, 8.5, 7.2, 7.8, 6.8, PRI);
      // Cream belly with a few darker feather marks, lighter facial disc.
      tint(b, (x, y, z) => z < 9 && ((x + 0.5 - cx) / 5) ** 2 + ((y + 0.5 - 5) / 4.4) ** 2 < 1, SEC);
      faceCells(b, [[8, 5], [12, 5], [10, 3], [9, 7], [11, 7]], SEC_D, 1);
      faceDisc(b, 7.5, 11.5, 3.7, SEC_L);
      faceDisc(b, 13.5, 11.5, 3.7, SEC_L);
      // Big eyes (the iris glows with glowing styles).
      eye(b, 7.5, 11.5, GLOW, 2.55, 1.5, -1, 1);
      eye(b, 13.5, 11.5, GLOW, 2.55, 1.5, -1, 1);
      // Hooked beak between the eyes.
      for (const [x, y, dz, v] of [
        [10, 9, 1, ACC],
        [9, 8, 1, ACC],
        [10, 8, 1, ACC],
        [11, 8, 1, ACC],
        [10, 8, 2, ACC_L],
        [10, 7, 1, ACC_D],
      ] as const) {
        const z0 = frontZ(b, x, y);
        if (z0 >= dz) b.set(x, y, z0 - dz, v);
      }
      // Ear tufts with team-colored tips.
      for (const s of [-1, 1]) {
        const ox = (x: number): number => (s < 0 ? x : b.sx - 1 - x);
        for (const [x, y] of [
          [6, 14],
          [7, 14],
          [6, 15],
          [7, 15],
          [5, 16],
          [6, 16],
        ])
          for (let z = 7; z < 10; z++) put(b, ox(x), y, z, PRI_D);
        for (let z = 7; z < 9; z++) b.set(ox(5), 17, z, TEAM);
      }
      // Little tail feathers and orange feet.
      blob(b, cx, 4, 15.5, 2.2, 2, 1.6, PRI_D, true);
      for (const x of [8, 9, 11, 12]) {
        for (let z = 7; z < 10; z++) put(b, x, 1, z, ACC);
        put(b, x, 0, 6, ACC_D);
        put(b, x, 0, 7, ACC);
      }
      // Wings hug the sides; they open outward when flapping.
      b.part('wingL');
      blob(b, 2.6, 7, 9.5, 1.7, 4.4, 3.1, PRI, true);
      b.part(null);
      tint(b, (x, y) => x < 4 && y <= 4, PRI_D);
      tint(b, (x, y) => x < 3 && y >= 9, LIGHT);
      mirrorPart(b, 'wingL', 'wingR');
      return {
        center: [cx, 8.5, 8.5],
        mouth: [cx, 8, 0],
        parts: {
          wingL: { pivot: [3.6, 11, 9.5], kind: 'flap', axis: 'z', amp: 0.2, offset: 0.16, rate: 4.2, sign: -1, work: 1.8, active: 1.6 },
          wingR: { pivot: [b.sx - 3.6, 11, 9.5], kind: 'flap', axis: 'z', amp: 0.2, offset: 0.16, rate: 4.2, sign: 1, work: 1.8, active: 1.6 },
        },
      };
    },
  },

  /** Medic: a floating leafy bud with a sweet face, leaf wings and a spinning flower on top. */
  sprout: {
    size: [23, 19, 15],
    build(b) {
      const cx = 11.5;
      blob(b, cx, 7.5, 7.5, 5.6, 5.6, 5.4, PRI);
      // Darker sepal crown around the stem, with a zigzag edge.
      tint(b, (x, y, z) => y >= 11 - ((x + z) % 2), PRI_D);
      // Face.
      faceCells(b, [[8, 7], [9, 7], [8, 8], [9, 8], [8, 9], [9, 9], [13, 7], [14, 7], [13, 8], [14, 8], [13, 9], [14, 9]], PUPIL);
      faceCells(b, [[8, 9], [13, 9]], GLINT, 1);
      faceCells(b, [[6, 6], [7, 6], [15, 6], [16, 6]], SEC_L, 1);
      faceCells(b, [[10, 5], [11, 5], [12, 5], [9, 6], [13, 6]], MOUTH, 1);
      // Stem with a team-colored ring.
      for (let y = 11; y < 15; y++) b.set(11, y, 7, PRI_D);
      for (const [x, z] of [
        [10, 6],
        [11, 6],
        [12, 6],
        [10, 7],
        [12, 7],
        [10, 8],
        [11, 8],
        [12, 8],
      ])
        b.set(x, 12, z, TEAM);
      // A tiny curly root.
      for (const [x, y, z] of [
        [11, 1, 8],
        [11, 0, 9],
        [12, 0, 10],
      ])
        put(b, x, y, z, PRI_D);
      // Flower: five petals around a glowing center; it spins like a rotor.
      b.part('flower');
      for (let x = 10; x < 13; x++) for (let z = 6; z < 9; z++) b.set(x, 15, z, GLOW).set(x, 16, z, GLOW);
      b.set(11, 17, 7, GLOW_HI);
      for (let k = 0; k < 5; k++) {
        const a = (k / 5) * Math.PI * 2 - Math.PI / 2;
        const px = cx + Math.cos(a) * 3.2;
        const pz = 7.5 + Math.sin(a) * 3.2;
        blob(b, px, 15.6, pz, 1.8, 1.05, 1.8, SEC, true);
        // Petal tips curl up into a cup.
        blob(b, cx + Math.cos(a) * 4.3, 16.6, 7.5 + Math.sin(a) * 4.3, 1.1, 0.6, 1.1, SEC_L, true);
      }
      b.part(null);
      tint(b, (x, y, z) => y >= 14 && y <= 16 && Math.hypot(x + 0.5 - cx, z + 0.5 - 7.5) > 4, SEC_L);
      // Leaf wings (lighter green with a dark midrib).
      b.part('leafL');
      for (let x = 0; x < 6; x++) {
        const t = (6.4 - (x + 0.5)) / 6.4;
        const hw = 0.5 + 2.9 * Math.pow(Math.sin(Math.PI * Math.min(1, t * 1.02)), 0.8);
        const lift = Math.round(t * t * 2.5);
        for (let z = 0; z < b.sz; z++) {
          const dz = Math.abs(z + 0.5 - 7.5);
          if (dz > hw) continue;
          // The leaf droops at its edges so it reads as a leaf from the side too.
          const edge = dz > hw * 0.62 ? 1 : 0;
          put(b, x, 7 + lift - edge, z, z === 7 ? PRI_D : LIGHT);
          if (dz < hw * 0.4 && x > 0) put(b, x, 6 + lift, z, PRI);
        }
      }
      b.part(null);
      mirrorPart(b, 'leafL', 'leafR');
      return {
        center: [cx, 7.5, 7.5],
        mouth: [cx, 15.5, 7.5],
        parts: {
          flower: { pivot: [cx, 15.5, 7.5], kind: 'spin', axis: 'y', amp: 0, rate: 0.35, work: 2, active: 5 },
          leafL: { pivot: [6.2, 7.5, 7.5], kind: 'flap', axis: 'z', amp: 0.38, offset: 0.3, rate: 1.7, sign: -1, active: 1.8 },
          leafR: { pivot: [b.sx - 6.2, 7.5, 7.5], kind: 'flap', axis: 'z', amp: 0.38, offset: 0.3, rate: 1.7, sign: 1, active: 1.8 },
        },
      };
    },
  },

  /** Digger: a striped bee-bot with goggles, a spinning drill nose and two little rotors. */
  drillbit: {
    size: [21, 17, 23],
    build(b) {
      const cx = 10.5;
      // Abdomen with dark stripes and a stinger.
      blob(b, cx, 8.5, 15.5, 4.6, 4.4, 5.3, PRI);
      tint(b, (_x, _y, z) => z >= 14 && Math.floor((z - 14) / 2) % 2 === 0, SEC);
      b.set(10, 8, 21, DARK_L).set(10, 8, 22, GLOW);
      // Dark head, fuzzy collar.
      blob(b, cx, 9.5, 8.5, 4.3, 4.3, 3.7, SEC);
      tint(b, (_x, _y, z, v) => z >= 11 && z <= 12 && (v === PRI || v === SEC), LIGHT);
      // Goggle eyes in metal rims.
      for (const ex of [8.5, 12.5]) {
        faceDisc(b, ex, 10.5, 2.35, ACC, 1, 1.6);
        faceDisc(b, ex, 10.5, 1.65, EYE_W);
        bulge(b, ex, 10.5, 1.65);
        faceCells(b, [[Math.floor(ex), 10], [Math.floor(ex), 9]], PUPIL, 1);
        faceCells(b, [[Math.floor(ex) - 1, 11]], GLINT, 1);
      }
      // Drill mount on the snout.
      faceDisc(b, cx, 6.5, 2.3, DARK_L, 1);
      // Antennae with glowing tips.
      for (const s of [-1, 1]) {
        const ox = (x: number): number => (s < 0 ? x : b.sx - 1 - x);
        put(b, ox(8), 13, 8, DARK);
        put(b, ox(8), 14, 7, DARK);
        put(b, ox(7), 15, 6, GLOW);
      }
      // Legs.
      for (const x of [8, 12]) for (let y = 6; y < 8; y++) put(b, x, y, 11, DARK);
      // Rotor masts (team-colored hubs spin with the blades).
      for (const x of [7, 13]) for (let y = 10; y < 15; y++) put(b, x, y, 12, DARK_L);
      b.part('rotorL');
      for (let x = 5; x < 10; x++) b.set(x, 15, 12, x === 7 ? TEAM : METAL_L);
      b.set(5, 15, 11, METAL).set(9, 15, 13, METAL);
      b.part(null);
      mirrorPart(b, 'rotorL', 'rotorR');
      // Drill: a cone with a spiral groove.
      b.part('drill');
      for (let z = 0; z < 6; z++) {
        const r = 0.5 + (1.3 * (z + 0.5)) / 6;
        for (let y = 3; y < 10; y++)
          for (let x = 7; x < 14; x++) {
            const dx = x + 0.5 - cx;
            const dy = y + 0.5 - 6.5;
            if (dx * dx + dy * dy > r * r) continue;
            const a = Math.atan2(dy, dx) / (Math.PI * 2);
            const band = Math.floor(a * 3 + (z + 0.5) * 0.5 + 10) % 2;
            b.set(x, y, z, z === 0 ? ACC_L : band ? ACC : ACC_D);
          }
      }
      b.part(null);
      return {
        center: [cx, 9, 12],
        mouth: [cx, 6.5, 0],
        parts: {
          drill: { pivot: [cx, 6.5, 3], kind: 'spin', axis: 'z', amp: 0, rate: 0.5, work: 16, active: 3 },
          rotorL: { pivot: [7.5, 15.5, 12.5], kind: 'spin', axis: 'y', amp: 0, rate: 5, sign: 1, work: 1.8 },
          rotorR: { pivot: [13.5, 15.5, 12.5], kind: 'spin', axis: 'y', amp: 0, rate: 5, sign: -1, work: 1.8, phase: 0.25 },
        },
      };
    },
  },

  /** Guard: a floating turtle shell shield with a glowing core, flippers and a peeking head. */
  bulwark: {
    size: [25, 23, 12],
    build(b) {
      const cx = 12.5;
      const cy = 10.5;
      const r2 = (x: number, y: number): number => (x + 0.5 - cx) ** 2 + (y + 0.5 - cy) ** 2;
      // Domed front plates, flat back, rim.
      blob(b, cx, cy, 8, 8, 8, 5.4, PRI, false, (_x, _y, z) => z < 8);
      for (let y = 0; y < b.sy; y++)
        for (let x = 0; x < b.sx; x++) {
          const d = r2(x, y);
          if (d <= 7.6 * 7.6) b.set(x, y, 8, SEC_D).set(x, y, 9, SEC_D);
          if (d <= 5.6 * 5.6) b.set(x, y, 10, DARK_L);
          if (d > 7.2 * 7.2 && d <= 8.4 * 8.4) b.set(x, y, 6, SEC).set(x, y, 7, SEC);
        }
      // Hexagonal plate seams.
      tint(b, (x, y, _z, v) => v === PRI && r2(x, y) > 3.4 * 3.4 && hexEdge(x + 0.5 - cx, y + 0.5 - cy, 3.3) < 0.9, PRI_D);
      // Glowing core in a bezel.
      faceDisc(b, cx, cy, 3.35, GLOW, 2, 2.5);
      faceDisc(b, cx, cy, 2.55, CORE);
      faceDisc(b, cx, cy, 1.5, CORE_HI);
      bulge(b, cx, cy, 2.55);
      // Team lights on the rim diagonals.
      for (const [x, y] of [
        [6, 4],
        [18, 4],
        [6, 16],
        [18, 16],
      ])
        b.set(x, y, 6, TEAM).set(x, y, 7, TEAM);
      // Head peeking over the top of the shell.
      b.part('head');
      blob(b, cx, 19.3, 6, 2.6, 2.2, 2.4, LIGHT, true);
      b.part(null);
      faceCells(b, [[11, 19], [13, 19], [11, 20], [13, 20]], PUPIL, 1);
      faceCells(b, [[11, 20], [13, 20]], GLINT, 1);
      faceCells(b, [[12, 18]], DARK_L, 1);
      // Flippers.
      b.part('finL');
      blob(b, 2.3, 9.5, 7, 2.3, 1.35, 1.25, LIGHT, true);
      b.part(null);
      tint(b, (x, y) => x === 0 || (x === 1 && y === 10), SEC_L);
      mirrorPart(b, 'finL', 'finR');
      return {
        center: [cx, cy, 6.5],
        mouth: [cx, cy, 2],
        parts: {
          head: { pivot: [cx, 18, 6.5], kind: 'sway', axis: 'y', amp: 0.45, rate: 0.22, work: 1, active: 2.5 },
          finL: { pivot: [4.2, 9.5, 7], kind: 'flap', axis: 'y', amp: 0.4, rate: 1.1, sign: -1, active: 2.2 },
          finR: { pivot: [b.sx - 4.2, 9.5, 7], kind: 'flap', axis: 'y', amp: 0.4, rate: 1.1, sign: 1, active: 2.2, phase: 0.5 },
        },
      };
    },
  },

  /** Attacker: a winged piranha with an underbite full of teeth, grumpy brows and a fan tail. */
  chomper: {
    size: [23, 21, 24],
    build(b) {
      const cx = 11.5;
      // Head/body, minus the lower front where the jaw sits.
      blob(b, cx, 10.5, 11, 5.6, 6.2, 7.4, PRI, false, (_x, y, z) => !(y < 8 && z < 10));
      tint(b, (_x, y, z) => y <= 8 && z >= 9, SEC);
      // Throat (dark mouth interior behind the lips) and upper teeth hanging into the gap row.
      for (let x = 9; x < 14; x++)
        for (let z = 7; z < 10; z++) {
          put(b, x, 7, z, MOUTH);
          put(b, x, 6, z, MOUTH);
        }
      for (let x = 8; x <= 14; x++) {
        const z0 = frontZ(b, x, 8);
        for (let z = z0 + 1; z0 >= 0 && z < 10; z++) b.recolor(x, 8, z, MOUTH);
      }
      for (const x of [9, 11, 13]) {
        const z0 = frontZ(b, x, 8);
        if (z0 >= 0) b.set(x, 7, z0, TOOTH);
      }
      // Grumpy eyes looking inward, heavy brows.
      for (const [ex, px] of [
        [8.5, 1],
        [14.5, -1],
      ]) {
        faceDisc(b, ex, 12.5, 2.15, EYE_W);
        bulge(b, ex, 12.5, 1.9);
        const pc = Math.floor(ex) + (px > 0 ? 0 : -1);
        faceCells(b, [[pc, 12], [pc + 1, 12], [pc, 11], [pc + 1, 11]], PUPIL, 1);
        faceCells(b, [[Math.floor(ex) - 1, 13]], GLINT, 1);
      }
      faceCells(b, [[7, 15], [8, 15], [9, 14], [10, 14], [15, 15], [14, 15], [13, 14], [12, 14]], PRI_D, 1);
      bulge(b, 8.8, 14.6, 1.2);
      bulge(b, 14.2, 14.6, 1.2);
      // Dorsal fin with a glowing edge and a team-colored tip.
      for (let z = 6; z < 15; z++) {
        const top = frontTop(b, 11, z);
        if (top < 0) continue;
        const h = Math.max(1, Math.round(3.6 - Math.abs(z + 0.5 - 9) * 0.5 + (z % 2 ? 0 : 0.4)));
        for (let k = 1; k <= h; k++) put(b, 11, top + k, z, k === h ? GLOW : ACC);
        if (z === 8) b.set(11, top + h, z, TEAM);
      }
      // Lower jaw (a part: opens for bites), with a white chin, tongue and lower teeth.
      b.part('jaw');
      blob(b, cx, 4.6, 8.6, 5, 2.2, 6, PRI, true, (_x, y, z) => y < 6 && z < 11);
      b.part(null);
      tint(b, (_x, y, z) => y <= 3 && z < 11, SEC);
      tint(b, (x, y, z) => y === 5 && z >= 5 && z < 10 && x >= 9 && x <= 13, MOUTH);
      b.part('jaw');
      for (const x of [8, 10, 12, 14]) {
        const z0 = frontZ(b, x, 5);
        if (z0 >= 0) b.set(x, 6, z0, TOOTH);
      }
      b.part(null);
      // Fan tail.
      b.part('tail');
      for (let z = 17; z < 23; z++) {
        const hh = 1 + (z - 17) * 0.85;
        for (let y = 0; y < b.sy; y++) {
          const dy = Math.abs(y + 0.5 - 10.5);
          if (dy > hh || (z >= 21 && dy < 1.4)) continue;
          put(b, 11, y, z, z >= 22 ? GLOW : y % 2 ? ACC : ACC_D);
          if (z === 17) {
            put(b, 10, y, z, ACC_D);
            put(b, 12, y, z, ACC_D);
          }
        }
      }
      b.part(null);
      // Little bat wings.
      b.part('wingL');
      for (let i = 0; i < 6; i++) {
        const x = 5 - i;
        const y0 = Math.round(11 + i * 0.6);
        const y1 = Math.round(14 + i * 0.5);
        for (let y = y0; y <= y1; y++) {
          if (i % 2 === 1 && y === y0) continue;
          for (let z = 10; z < 12; z++) put(b, x, y, z, y === y1 ? PRI_D : ACC_D);
        }
      }
      b.part(null);
      mirrorPart(b, 'wingL', 'wingR');
      return {
        center: [cx, 10.5, 11],
        mouth: [cx, 6, 3],
        parts: {
          jaw: { pivot: [cx, 6.5, 10.5], kind: 'jaw', axis: 'x', amp: 0.75, rate: 3, sign: -1 },
          tail: { pivot: [cx, 10.5, 17.2], kind: 'sway', axis: 'y', amp: 0.38, rate: 1.4, work: 2.4 },
          wingL: { pivot: [6.2, 12.5, 11], kind: 'flap', axis: 'z', amp: 0.55, offset: 0.15, rate: 3, sign: -1, work: 1.9 },
          wingR: { pivot: [b.sx - 6.2, 12.5, 11], kind: 'flap', axis: 'z', amp: 0.55, offset: 0.15, rate: 3, sign: 1, work: 1.9 },
        },
      };
    },
  },
};

/** Highest solid voxel in a column (x, z), or -1. */
function frontTop(b: PB, x: number, z: number): number {
  for (let y = b.sy - 1; y >= 0; y--) if (b.get(x, y, z)) return y;
  return -1;
}

// Building and caching ---------------------------------------------------------------------------

interface BaseModel {
  sx: number;
  sy: number;
  sz: number;
  grid: Uint8Array;
  parts: PetPartModel[];
  meta: PetDesignMeta;
  radius: number;
}

const baseCache = new Map<string, BaseModel>();

function buildBase(id: string): BaseModel {
  const hit = baseCache.get(id);
  if (hit) return hit;
  const d = DESIGNS[id] ?? DESIGNS.owlet;
  const [sx, sy, sz] = d.size;
  const b = new ModelBuilder<PetPartName>(sx, sy, sz);
  const meta = d.build(b);
  const parts: PetPartModel[] = [];
  for (const [name, grid] of b.parts) {
    const pm = meta.parts[name];
    if (!pm) continue;
    parts.push({ ...pm, name, grid });
  }
  let r2 = 0;
  const [cx, cy, cz] = meta.center;
  for (let y = 0; y < sy; y++)
    for (let z = 0; z < sz; z++)
      for (let x = 0; x < sx; x++) {
        if (!b.grid[b.index(x, y, z)]) continue;
        const dx = Math.max(Math.abs(x - cx), Math.abs(x + 1 - cx));
        const dy = Math.max(Math.abs(y - cy), Math.abs(y + 1 - cy));
        const dz = Math.max(Math.abs(z - cz), Math.abs(z + 1 - cz));
        r2 = Math.max(r2, dx * dx + dy * dy + dz * dz);
      }
  const base: BaseModel = { sx, sy, sz, grid: b.grid, parts, meta, radius: Math.sqrt(r2) * PET_VOXEL };
  baseCache.set(id, base);
  return base;
}

/** Palette for a pet in given colors: shades derived like weapon palettes, plus eyes/mouth/team/core. */
function petPalette(colors: [number, number, number], glow: number | undefined, team: number): { palette: number[]; emissive: number[] } {
  const [pri, sec, acc] = colors;
  const g = glow ?? acc;
  const p = new Array<number>(PET_SLOT_COUNT).fill(0);
  p[PRI] = pri;
  p[LIGHT] = tone(pri, 1.24);
  p[PRI_D] = tone(pri, 0.72);
  p[SEC] = sec;
  p[SEC_L] = tone(sec, 1.22);
  p[SEC_D] = tone(sec, 0.72);
  p[ACC] = acc;
  p[ACC_L] = tone(acc, 1.22);
  p[ACC_D] = tone(acc, 0.72);
  p[GLOW] = g;
  p[GLOW_HI] = mix(g, 0xffffff, 0.55);
  p[DARK] = 0x2a2a30;
  p[DARK_L] = 0x44454d;
  p[METAL] = 0xa2aab5;
  p[METAL_L] = 0xd6dce4;
  p[METAL_D] = 0x666d78;
  p[WHITE] = 0xeef0f2;
  p[RUBBER] = 0x18181c;
  p[EYE_W] = 0xf6f7fa;
  p[PUPIL] = 0x1c1d26;
  p[GLINT] = 0xffffff;
  p[TEAM] = team >= 0 && team < TEAM_COLORS.length ? TEAM_COLORS[team] : tone(acc, 1.15);
  p[MOUTH] = 0x3a2436;
  p[TOOTH] = 0xf6f2e6;
  const core = glow ?? mix(acc, 0xffffff, 0.2);
  p[CORE] = core;
  p[CORE_HI] = mix(core, 0xffffff, 0.6);
  const emissive = [GLINT, CORE, CORE_HI];
  if (team >= 0 && team < TEAM_COLORS.length) emissive.push(TEAM);
  if (glow !== undefined) emissive.push(GLOW, GLOW_HI);
  return { palette: p, emissive };
}

const cache = new Map<string, PetModel>();

/** Pet definition by id or wire num. */
export function petDef(pet: PetDef | string | number): PetDef | undefined {
  return typeof pet === 'string' ? PET_BY_ID[pet] : typeof pet === 'number' ? PET_BY_NUM[pet] : pet;
}

/**
 * Builds (and caches) a pet's voxel model in a style (unknown/empty = the pet's own colors).
 * `team` (0/1) lights the team badge in team modes; -1 = no team (FFA, menus).
 */
export function buildPetModel(pet: PetDef | string | number, styleId?: string, team = -1): PetModel | null {
  const def = petDef(pet);
  if (!def) return null;
  const t = team === 0 || team === 1 ? team : -1;
  const { colors, glow } = petColors(def, styleId);
  const key = `${def.id}|${colors.join(',')}|${glow ?? -1}|${t}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const base = buildBase(def.id);
  const { palette, emissive } = petPalette(colors, glow, t);
  const model: PetModel = {
    id: def.id,
    num: def.num,
    sx: base.sx,
    sy: base.sy,
    sz: base.sz,
    grid: base.grid,
    palette,
    emissive,
    center: base.meta.center,
    mouth: base.meta.mouth,
    parts: base.parts,
    radius: base.radius,
  };
  cache.set(key, model);
  return model;
}
